import { ButtonGroup } from "@hortis/ui/button/button-group";
import { colors } from "@hortis/ui/colors";
import { LoadingPage } from "@hortis/ui/loading";
import { toast } from "@hortis/ui/sonner";
import { Outlet, createFileRoute } from "@tanstack/react-router";
import {
  type LocationOrderByInput,
  type PlantMaterialsOrderByInput,
  PlantMaterialsSearchColumn,
  useGetCollectionLocationGeometryQuery,
  useGetMapConfigQuery,
} from "generated/graphql";
import { type MapMouseEvent } from "mapbox-gl";
import { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Layer } from "react-map-gl";
import { CenterCollection } from "src/components/map/actions/center-collection";
import { CenterCurrentPosition } from "src/components/map/actions/center-current-position";
import { LocationDrawingButtons } from "src/components/map/actions/location-drawing-buttons";
import { MapZoomButtons } from "src/components/map/actions/map-zoom-buttons";
import { SelectLayers } from "src/components/map/actions/select-layers";
import { MapObserver } from "src/components/map/cypress-map-observer";
import { DrawControl } from "src/components/map/draw-control";
import { BaseMapLayers } from "src/components/map/layers/base-map-layers";
import { LocationPolygonViewingLayer } from "src/components/map/location-bed-polygons/location-bed-polygon-layer";
import { LocationPopup } from "src/components/map/location-bed-polygons/location-popup";
import { parseLocationsGeometry } from "src/components/map/location-bed-polygons/parse-geometry";
import { useLocationBedDrawing } from "src/components/map/location-bed-polygons/use-location-bed-polygons";
import {
  getTopLevelFeature,
  useMapCursorStyling,
} from "src/components/map/location-bed-polygons/use-map-cursor-styling";
import { CoordinateMenu } from "src/components/map/material-clusters/coordinate-menu";
import { themedHighlightedUnclusteredPointLayer } from "src/components/map/material-clusters/material-cluster-layers";
import { MaterialPopup } from "src/components/map/material-clusters/material-popup";
import { useClusters } from "src/components/map/material-clusters/use-clusters";
import { MapOverlay } from "src/components/map/overlays/map-overlay";
import { NoInitialViewMapOverlay } from "src/components/map/overlays/no-initial-view-map-overlay";
import { SetInitialViewMapOverlay } from "src/components/map/overlays/set-initial-view-map-overlay";
import GeometryStyles from "src/components/map/stories/default-styles";
import { useThemedLayer } from "src/components/map/theme-layer-hook";
import { useMaplibre } from "src/components/map/utils";
import { CenterOverlay } from "src/features/collection-map/components/center-overlay";
import CustomOverlay from "src/features/collection-map/components/custom-overlay";
import { EditDetailsOverlay } from "src/features/collection-map/components/edit-details-overlay";
import { EditLocationPolygonOverlay } from "src/features/collection-map/components/edit-location-polygon-overlay";
import { FiltersOverlay } from "src/features/collection-map/components/filters-overlay/filters-overlay";
import { MapLocationList } from "src/features/collection-map/components/location-list/map-location-list";
import { MapListOverlay } from "src/features/collection-map/components/map-list";
import { useMapOptionsStore } from "src/features/collection-map/components/map-options-store";
import { MapMaterialList } from "src/features/collection-map/components/material-list/map-material-list";
import { ShowListButton } from "src/features/collection-map/components/overlay-ui/buttons";
import { NewMaterialDrawer } from "src/features/collection-map/components/overlay-ui/new-material-drawer";
import { OverlayWithSearch } from "src/features/collection-map/components/overlay-ui/search-row";
import { useGeometryLayers } from "src/features/collection-map/components/use-geometry-layers";
import { useLatLongZoom } from "src/features/collection-map/components/use-lat-long-zoom";
import { useMapPadding } from "src/features/collection-map/components/use-map-padding";
import { useMapSearch } from "src/features/collection-map/components/use-map-search";
import {
  getCollectionGeometryDocument,
  getMapConfigDocument,
} from "src/features/collection-map/graphql";
import { MapContext, useMapStore } from "src/features/collection-map/map-store";
import { mapId } from "src/features/collection-map/utils";
import { createMaterialNumber } from "src/features/collection/components/plant-materials/material-number";
import { useLocationFilters } from "src/features/filters/locations/use-location-filters";
import { useMaterialFilters } from "src/features/filters/plant-materials/use-material-filters";
import { useAccessRole } from "src/features/permissions/use-access-role";
import { useGqlQuery } from "src/utils/hooks/graphql";
import { useMobile } from "src/utils/hooks/media-query";
import { useToggle } from "src/utils/hooks/toggle";
import withAuthenticationRequired from "src/utils/with-authentication-required";
import { match } from "ts-pattern";
import { z } from "zod";
import { Map } from "../../../../components/map";

const schema = z.object({
  searchValue: z.coerce.string().optional(),
  searchField: z
    .nativeEnum(PlantMaterialsSearchColumn)
    .optional()
    .catch(PlantMaterialsSearchColumn.ScientificName),
  latLng: z
    .object({ lat: z.number(), lng: z.number() })
    .optional()
    .catch(undefined),
  zoom: z.number().optional().catch(undefined),
});

export const Route = createFileRoute("/_layout/site/$siteSlug/map")({
  component: withAuthenticationRequired(Component, {
    onRedirecting: function OnRedirect() {
      return <Component />;
    },
  }),
  validateSearch: (search) => schema.parse(search),
});

function Component() {
  const { [mapId]: ourMap } = useMaplibre();
  const isMobile = useMobile();
  const { searchTerm, handleSubmit, handleClear } = useMapSearch();
  const materialFilters = useMaterialFilters({
    initialState: {
      excludeAbsentMaterial: true,
    },
  });

  const locationFilters = useLocationFilters();
  const [locationSearchTerm, setLocationSearchTerm] = useState("");
  const [newLocationDrawerOpen, setNewLocationDrawerOpen] = useState(false);

  const { subdomain } = Route.useRouteContext();
  const { siteSlug } = Route.useParams();
  const variables = {
    organisationSubdomain: subdomain,
    collectionSiteSlug: siteSlug,
    searchTerm,
    filters: materialFilters.preparedFilters,
  };
  const {
    data: geometry,
    isFetching: geometryFetching,
    error: geometryError,
  } = useGqlQuery({
    query: getCollectionGeometryDocument,
    variables,
  });
  const navigate = Route.useNavigate();
  const {
    data,
    isFetching: fetching,
    error,
    refetch: refetchConfig,
  } = useGqlQuery({
    query: getMapConfigDocument,
    variables: {
      organisationSubdomain: subdomain,
      collectionSiteSlug: siteSlug,
    },
  });

  const handleTabChange = () => {
    // eslint-disable-next-line sonarjs/no-duplicate-string
    if (store.matchedRoute.route === "locations-list") {
      setLocationListCursor(null);
    } else {
      setMaterialListCursor(null);
    }
  };

  const { locationPolygonsEnabled } = useMapOptionsStore();

  const key = data?.org?.map?.key;
  const site = data?.org?.site;
  const initialViewState = useMemo(
    () =>
      site?.mapCenterLongitude != null && site.mapCenterLatitude != null
        ? {
            latitude: site.mapCenterLatitude,
            longitude: site.mapCenterLongitude,
            zoom: site.mapZoomLevel ?? undefined,
          }
        : undefined,
    [site],
  );
  const baseMap = data?.org?.site?.baseMap ?? null;
  const drawRef = useRef<MapboxDraw>(undefined);
  const [downloadRequested, toggleDownloadRequested] = useToggle();

  const [
    { data: locationGeometryData, error: locationGeometryError },
    refetchLocationGeometry,
  ] = useGetCollectionLocationGeometryQuery({
    variables: {
      organisationSubdomain: subdomain,
      collectionSiteSlug: siteSlug,
      searchTerm: locationSearchTerm,
    },
  });

  useEffect(() => {
    if (locationGeometryError != null) {
      toast.error("Failed to load location geometry");
    }
  }, [locationGeometryError]);

  const store = useMapStore({
    refetchSite: refetchConfig,
    drawRef,
    refetchLocationGeometry,
    geometryQueryVariables: variables,
  });

  const locationGeometry = parseLocationsGeometry(
    locationGeometryData?.org?.site?.locationsGeometry,
  );

  useEffect(() => {
    if (site != null && initialViewState == null) {
      store.openMissingInitialView();
    }
  }, [site, initialViewState, store]);

  const {
    popupProps: materialPopupProps,
    handleClick: handleClustersClick,
    clusterLayer: [clusterLayer, clusterConfig],
    clusterCountLayer: [clusterCountLayer, clusterCountConfig],
    onMouseMove: clustersOnMouseMove,
    onMouseLeave: clustersOnMouseLeave,
  } = useClusters({
    mapId,
    onPointClick: (material) => {
      if (material != null) {
        void navigate({
          // eslint-disable-next-line sonarjs/no-duplicate-string
          from: "/site/$siteSlug/map",
          to: "/site/$siteSlug/map/material/$materialNumber",
          params: {
            materialNumber: createMaterialNumber(
              material.accessionNumber,
              material.materialQualifier,
            ),
          },
        });
      }
    },
    closeMaterialDetails: () => {
      void navigate({
        to: "/site/$siteSlug/map",
        from: "/site/$siteSlug/map",
      });
    },
    ...store,
  });

  const {
    handleClick: handlePolygonClick,
    onMouseMove: onLocationPolygonMouseMove,
    onMouseLeave: onLocationPolygonMouseLeave,
    popupProps: locationPopupProps,
  } = useLocationBedDrawing({
    mapId,
    mode: store.mode,
  });

  const { cursor, handleMouseMove, topLevelFeature } = useMapCursorStyling({
    mapId,
    mode: store.mode,
  });

  const onMouseMove = (event: MapMouseEvent) => {
    if (store.mode === "editMaterial") {
      return;
    }
    handleMouseMove(event);
    if (locationPolygonsEnabled) {
      if (
        topLevelFeature?.geometry.type === "Polygon" ||
        topLevelFeature?.geometry.type === "MultiPolygon"
      ) {
        onLocationPolygonMouseMove(event);
      } else if (topLevelFeature?.geometry.type === "Point") {
        clustersOnMouseMove(event);
      }
    } else {
      clustersOnMouseMove(event);
    }
  };

  const onMouseLeave = () => {
    if (locationPolygonsEnabled) {
      if (
        topLevelFeature?.geometry.type === "Polygon" ||
        topLevelFeature?.geometry.type === "MultiPolygon"
      ) {
        onLocationPolygonMouseLeave();
      } else if (topLevelFeature?.geometry.type === "Point") {
        clustersOnMouseLeave();
      }
    } else {
      clustersOnMouseLeave();
    }
  };

  const onMouseClick = (event: MapMouseEvent) => {
    store.closeCoordinateMenu();

    const { topLevelFeature } = getTopLevelFeature({
      event,
      mode: store.mode,
      ourMap,
    });

    if (
      topLevelFeature?.geometry.type === "Polygon" ||
      topLevelFeature?.geometry.type === "MultiPolygon"
    ) {
      store.expandSidebar();
      handlePolygonClick(event, topLevelFeature);
    } else if (topLevelFeature?.geometry.type === "Point") {
      if (isMobile && topLevelFeature.properties.cluster !== true) {
        store.expandSidebar();
      }
      handleClustersClick(event);
    } else if (store.matchedRoute.route === "material") {
      if (isMobile) {
        store.collapseSidebar();
      }
      void navigate({
        to: "/site/$siteSlug/map/materials",
        from: "/site/$siteSlug/map",
      });
    } else if (store.matchedRoute.route === "location") {
      if (isMobile) {
        store.collapseSidebar();
      }
      void navigate({
        to: "/site/$siteSlug/map/locations",
        from: "/site/$siteSlug/map",
      });
    } else if (store.coordinateMenuProps == null) {
      store.openCoordinateMenu({
        latitude: event.lngLat.lat,
        longitude: event.lngLat.lng,
      });
    }
  };

  const { updateLatLng } = useLatLongZoom({ ourMap });

  const [highlightedLayer, highlightedProps] = useThemedLayer(
    useMemo(() => {
      return themedHighlightedUnclusteredPointLayer(
        store.matchedRoute.route === "material" &&
          store.matchedRoute.number != null
          ? {
              accessionNumber: store.matchedRoute.number.accessionNumber,
              materialQualifier: store.matchedRoute.number.qualifier,
            }
          : undefined,
      );
    }, [store.matchedRoute]),
    "highlighted-layer",
    store.mode === "editMaterial" ? 0.5 : undefined,
  );

  const [geometryLayers, interactiveLayerIds] = useGeometryLayers({
    geometry,
    loading: geometryFetching,
    error: geometryError,
    layers: [clusterLayer, clusterCountLayer, highlightedLayer],
    layerConfigs: [clusterConfig, clusterCountConfig, highlightedProps],
    coordinateMenuProps: store.coordinateMenuProps,
  });

  const filteredInteractiveLayers = [
    ...interactiveLayerIds,
    "location-polygon-fill",
  ].filter((l) => ourMap?.getLayer(l) != null);

  const padding = useMapPadding({
    drawerCollapsed: !store.sidebarOpen,
    mode: store.mode,
  });
  const [materialOrderBy, setMaterialOrderBy] = useState<
    PlantMaterialsOrderByInput | undefined
  >();
  const [locationOrderBy, setLocationOrderBy] = useState<
    LocationOrderByInput | undefined
  >();

  const { canEdit } = useAccessRole();
  const [materialListCursor, setMaterialListCursor] = useState<string | null>(
    null,
  );
  const [locationListCursor, setLocationListCursor] = useState<string | null>(
    null,
  );
  const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);

  const hasValidData = site != null && key != null;

  return (
    <MapContext.Provider value={store}>
      <div
        className="relative flex h-[calc(calc(var(--vh,1vh)*100)-56px)] min-h-full flex-1 flex-col lg:h-[calc(var(--vh,1vh)*100)] [&_.mapboxgl-canvas]:outline-hidden  [&_.mapboxgl-map]:flex-1 [&_.mapboxgl-map]:overflow-visible [&_.mapboxgl-popup-content]:rounded-xl! [&_.mapboxgl-popup-content]:bg-[none]! [&_.mapboxgl-popup-content]:p-0! [&_.mapboxgl-popup-content]:shadow-none! [&_.mapboxgl-popup-content]:animate-in [&_.mapboxgl-popup-content]:fade-in-10 [&_.mapboxgl-popup-content]:slide-in-from-bottom-2 [&_.mapboxgl-popup-tip]:opacity-0! [&_.mapboxgl-popup]:max-w-sm! [&_.mapboxgl-popup]:bg-[none]! [&_.maplibregl-control-container]:font-sans! [&_.maplibregl-control-container]:font-medium! [&_.maplibregl-ctrl-attrib-button]:bottom-0! [&_.maplibregl-ctrl-attrib-button]:top-auto! [&_.maplibregl-ctrl-attrib]:max-w-44! [&_.maplibregl-ctrl-attrib]:rounded-full! [&_.maplibregl-ctrl-attrib]:bg-white/70! [&_.maplibregl-ctrl-attrib]:backdrop-blur-sm! sm:[&_.maplibregl-ctrl-attrib]:max-w-none! [&_.maplibregl-ctrl-bottom-right]:bottom-1! [&_.maplibregl-ctrl-bottom-right]:right-1!"
        data-cy="map-container"
      >
        <Helmet>
          <title>Map | Hortis</title>
        </Helmet>
        {!hasValidData && fetching && <LoadingPage />}
        {!hasValidData && error != null && <>Error: {error.message}</>}
        {hasValidData && (
          <Map
            mapStyle={`https://api.maptiler.com/maps/basic-v2/style.json?key=${key}`}
            attributionControl={false}
            initialViewState={{
              padding,
              ...initialViewState,
              ...(store.latLng == null
                ? {}
                : { latitude: store.latLng.lat, longitude: store.latLng.lng }),
              ...(store.zoom == null ? {} : { zoom: store.zoom }),
            }}
            id={mapId}
            onClick={onMouseClick}
            interactiveLayerIds={filteredInteractiveLayers}
            onMouseMove={isMobile ? undefined : onMouseMove}
            onMouseLeave={onMouseLeave}
            cursor={cursor}
            onMoveEnd={updateLatLng}
          >
            <MapObserver testIdPrefix="collection-map-observer" />
            {materialPopupProps != null && (
              <MaterialPopup {...materialPopupProps} />
            )}
            {materialPopupProps == null && locationPopupProps != null && (
              <LocationPopup {...locationPopupProps} />
            )}

            <BaseMapLayers baseMap={baseMap} />

            {(store.mode === "view" || store.mode === "editMaterial") &&
              geometryLayers}

            {locationGeometry != null &&
              !locationGeometryError &&
              locationPolygonsEnabled && (
                <LocationPolygonViewingLayer
                  editMode={store.mode === "editLocation"}
                  editingLocation={store.editingLocation}
                  geometry={locationGeometry}
                  currentHoveredFeature={topLevelFeature}
                />
              )}

            {match(store.mode)
              // eslint-disable-next-line sonarjs/cognitive-complexity
              .with("view", () => (
                <MapOverlay>
                  {store.coordinateMenuProps != null && (
                    <CoordinateMenu
                      requestNewMaterial={store.openNewMaterial}
                      closeCoordinateMenu={store.closeCoordinateMenu}
                      {...store.coordinateMenuProps}
                    />
                  )}

                  {store.matchedRoute.route !== "material" &&
                    store.matchedRoute.route !== "location" && (
                      <MapListOverlay
                        onDownloadRequest={
                          store.matchedRoute.route === "locations-list"
                            ? undefined
                            : toggleDownloadRequested
                        }
                        isContextMenuOpen={isContextMenuOpen}
                        handleTabChange={handleTabChange}
                        tabValue={
                          store.matchedRoute.route === "locations-list"
                            ? "locations-list"
                            : "materials-list"
                        }
                        setDrawerCollapsed={(collapsed) => {
                          if (collapsed) {
                            store.collapseSidebar();
                          } else {
                            store.expandSidebar();
                          }
                        }}
                        open={store.sidebarOpen}
                        onCreateNewClick={
                          store.matchedRoute.route === "locations-list"
                            ? () => {
                                setNewLocationDrawerOpen(true);
                              }
                            : store.openNewMaterial
                        }
                      >
                        {store.matchedRoute.route === "locations-list" ? (
                          <MapLocationList
                            newLocationDrawerOpen={newLocationDrawerOpen}
                            setNewLocationDrawerOpen={setNewLocationDrawerOpen}
                            orderBy={locationOrderBy}
                            setOrderBy={setLocationOrderBy}
                            searchTerm={locationSearchTerm}
                            setSearchTerm={setLocationSearchTerm}
                            cursor={locationListCursor}
                            setCursor={setLocationListCursor}
                            locationFilters={locationFilters}
                          />
                        ) : (
                          <MapMaterialList
                            downloadRequested={downloadRequested}
                            resetDownloadRequested={toggleDownloadRequested}
                            setIsContextMenuOpen={setIsContextMenuOpen}
                            searchTerm={searchTerm}
                            cursor={materialListCursor}
                            setCursor={setMaterialListCursor}
                            handleSearchSubmit={(value, field) => {
                              setMaterialListCursor(null);
                              handleSubmit(value, field);
                            }}
                            handleSearchClear={(field) => {
                              setMaterialListCursor(null);
                              handleClear(field);
                            }}
                            materialFilters={materialFilters}
                            orderBy={materialOrderBy}
                            setOrderBy={setMaterialOrderBy}
                          />
                        )}
                      </MapListOverlay>
                    )}
                  <FiltersOverlay
                    setOpen={(open) => {
                      if (open) {
                        store.openFilters();
                      } else {
                        store.closeFilters();
                      }
                    }}
                    materialFilters={materialFilters}
                    open={store.filtersOpen}
                  />
                  {store.matchedRoute.route === "material" && <Outlet />}
                  {store.matchedRoute.route === "location" && <Outlet />}
                  <div className="flex flex-1 flex-col p-2.5">
                    <div className="pointer-events-none flex flex-1 flex-col items-stretch gap-3 lg:flex-row lg:items-start lg:justify-between">
                      {isMobile && (
                        <OverlayWithSearch
                          searchTerm={searchTerm}
                          handleSearchSubmit={handleSubmit}
                          handleSearchClear={handleClear}
                          toggleFilters={() => {
                            if (store.filtersOpen) {
                              store.closeFilters();
                            } else {
                              store.openFilters();
                            }
                          }}
                          materialFilters={materialFilters}
                        />
                      )}
                      {!isMobile && !store.sidebarOpen && (
                        <ShowListButton
                          onClick={() => {
                            if (store.sidebarOpen) {
                              store.collapseSidebar();
                            } else {
                              store.expandSidebar();
                            }
                          }}
                          testId="show-list-button"
                        />
                      )}
                      <div className="hidden flex-1 lg:block" />
                      <div className="flex flex-col gap-3 self-end lg:self-auto">
                        <SelectLayers baseMap={baseMap} />
                        <ButtonGroup
                          direction="column"
                          className="shadow-mg pointer-events-auto border-none"
                        >
                          {initialViewState == null ? undefined : (
                            <CenterCollection {...initialViewState} />
                          )}
                          <CenterCurrentPosition />
                        </ButtonGroup>
                        <MapZoomButtons />
                      </div>
                    </div>
                    <div className="flex justify-between gap-3">
                      {isMobile && (
                        <ShowListButton
                          onClick={() => {
                            if (store.sidebarOpen) {
                              store.collapseSidebar();
                            } else {
                              store.expandSidebar();
                            }
                          }}
                          testId="show-list-button-mobile"
                        />
                      )}
                    </div>
                  </div>
                  {canEdit && (
                    <NewMaterialDrawer
                      open={store.newMaterialDrawerOpen}
                      onOpenChange={(open: boolean) => {
                        if (!open) {
                          store.cancelNewMaterial();
                        }
                      }}
                      onSubmit={store.confirmNewMaterial}
                      initialValues={{
                        position:
                          store.newMaterialCoordinates == null
                            ? null
                            : {
                                latitude: String(
                                  store.newMaterialCoordinates.latitude,
                                ),
                                longitude: String(
                                  store.newMaterialCoordinates.longitude,
                                ),
                              },
                      }}
                    />
                  )}
                </MapOverlay>
              ))
              .with("editMaterial", () => (
                <MapOverlay className="flex-1 p-2.5 md:flex-col">
                  <Layer
                    id="hover-label"
                    type="symbol"
                    source="geometry"
                    filter={["!", ["has", "point_count"]]}
                    layout={{
                      "text-field": [
                        "get",
                        "materialNumber",
                        ["get", "adHoc", ["properties"]],
                      ],
                      "text-size": 12,
                      "text-offset": [0, 1.5],
                      "text-anchor": "top",
                    }}
                    paint={{
                      "text-color": colors.primary[700],
                      "text-halo-color": colors.white,
                      "text-halo-width": 1,
                    }}
                  />
                  <CustomOverlay>
                    <CenterOverlay />
                  </CustomOverlay>
                  <div className="pointer-events-none flex flex-1 flex-col items-end gap-3 self-end lg:self-auto">
                    <SelectLayers baseMap={baseMap} />
                    <ButtonGroup
                      direction="column"
                      className="shadow-mg border-none"
                    >
                      {initialViewState == null ? undefined : (
                        <CenterCollection {...initialViewState} />
                      )}
                      <CenterCurrentPosition />
                    </ButtonGroup>
                    <MapZoomButtons />
                  </div>
                  {store.editingMaterial != null && (
                    <EditDetailsOverlay
                      editingMaterial={store.editingMaterial}
                      cancelEdit={store.cancelEdit}
                      confirmEdit={store.confirmEdit}
                    />
                  )}
                </MapOverlay>
              ))
              .with("editLocation", () => (
                <MapOverlay className="flex-1 p-2.5 md:flex-col">
                  <div className="flex">
                    {drawRef.current != null && (
                      <div className="pointer-events-none self-start">
                        <LocationDrawingButtons drawRef={drawRef} />
                      </div>
                    )}
                    <div className="pointer-events-none flex flex-1 flex-col items-end gap-3 self-end lg:self-auto">
                      <SelectLayers baseMap={baseMap} />
                      <ButtonGroup
                        direction="column"
                        className="border-none shadow-md"
                      >
                        {initialViewState == null ? undefined : (
                          <CenterCollection {...initialViewState} />
                        )}
                        <CenterCurrentPosition />
                      </ButtonGroup>
                      <MapZoomButtons />
                    </div>
                  </div>

                  {store.editingLocation != null && (
                    <>
                      <DrawControl
                        ref={drawRef}
                        initialGeometry={store.editingLocation.geometry}
                        userProperties={true}
                        position="top-left"
                        displayControlsDefault={false}
                        styles={GeometryStyles({ color: "warning" })}
                        defaultMode={
                          store.editingLocation.geometry == null
                            ? "draw_polygon"
                            : "simple_select"
                        }
                      />
                      <EditLocationPolygonOverlay
                        editingLocation={store.editingLocation}
                        cancelEdit={store.cancelEditLocation}
                        confirmEdit={store.confirmEditLocationGeometry}
                      />
                    </>
                  )}
                </MapOverlay>
              ))
              .with("noInitialView", () => (
                <NoInitialViewMapOverlay
                  setInitialView={store.setInitialView}
                />
              ))
              .with("setInitialView", () => (
                <SetInitialViewMapOverlay
                  confirmInitialView={() => store.confirmInitialView(site.id)}
                  baseMap={baseMap}
                />
              ))
              .exhaustive()}
          </Map>
        )}
      </div>
    </MapContext.Provider>
  );
}
